import { capitalizeFirstLetter } from '@/shared/utils/general.util';

const CONNECTORS = new Set(['a', 'al', 'con', 'de', 'del', 'e', 'el', 'en', 'la', 'las', 'los', 'o', 'para', 'por', 'u', 'y']);

const LEGAL_FORMS = new Set(['AC', 'CV', 'IAP', 'RL', 'SA', 'SAB', 'SAPI', 'SAS', 'SC', 'SCP', 'SOFOM', 'SPR', 'SRL']);

const DOTTED_ABBREVIATION = /^(\p{Lu}\.)+\p{Lu}?$/u;
const HAS_VOWEL = /[AEIOUÁÉÍÓÚÜ]/;

const formatCore = (core: string, isFirst: boolean): string => {
  if (!/\p{L}/u.test(core)) return core;
  if (DOTTED_ABBREVIATION.test(core) || LEGAL_FORMS.has(core.replace(/\./g, ''))) return core;
  if (core.length > 1 && !HAS_VOWEL.test(core)) return core;
  const lower = core.toLowerCase();
  if (!isFirst && CONNECTORS.has(lower)) return lower;
  return core.split('-').map(capitalizeFirstLetter).join('-');
};

const formatToken = (token: string, isFirst: boolean): string => {
  const match = token.match(/^([^\p{L}\d]*)(.*?)([^\p{L}\d.]*)$/u);
  if (!match) return token;
  const [, leading, core, trailing] = match;
  return `${leading}${formatCore(core, isFirst)}${trailing}`;
};

/**
 * Nombre fiscal legible para pantalla.
 *
 * El SAT guarda nombres y razones sociales en mayúsculas, y así llegan del
 * perfil, del sello y de los clientes guardados. Aquí se muestran con mayúscula
 * inicial por palabra, conectores en minúscula y siglas y formas legales
 * (`S.A. de C.V.`, `SAPI`) intactas.
 *
 * Solo presentación: el comprobante sigue llevando el nombre tal cual lo tiene
 * la constancia. Un nombre que ya trae minúsculas lo capturó una persona y se
 * respeta como viene.
 */
export const displayFiscalName = (name?: string | null): string => {
  const value = name?.trim() ?? '';
  if (!value || value !== value.toUpperCase()) return value;
  let wordIndex = 0;
  return value
    .split(/(\s+)/)
    .map((token) => (/^\s+$/.test(token) ? token : formatToken(token, wordIndex++ === 0)))
    .join('');
};
